import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import { rem } from '../../../../components/panels/panelLayout';
import { getDashboardPanelTokens } from '../dashboardPanelTokens';
import { TileHeading } from '../TileHeading';

interface RailPromoCardProps {
  readonly title: string;
  readonly description: React.ReactNode;
  /** Optional brand mark, shown small beside the copy rather than as artwork. */
  readonly icon?: React.ReactNode;
  /** The card's single call to action, pinned to the bottom. */
  readonly action: React.ReactNode;
}

/**
 * A rail card that offers one thing: Support, eRiskHub and the quick actions.
 *
 * Shaped like every other tile on this dashboard -- heading, supporting copy,
 * then the action -- rather than the icon-left split these used to have, where
 * an oversized glyph took a quarter of the width and the button stretched the
 * full remainder. The action sits on the bottom edge so it lines up with the
 * card stacked beside it.
 */
export function RailPromoCard({
  title,
  description,
  icon,
  action,
}: Readonly<RailPromoCardProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Box display="flex" alignItems="center" gap={rem(8)}>
        {icon ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              '& svg, & img': { width: 'auto', height: rem(18) },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <TileHeading>{title}</TileHeading>
      </Box>

      <Typography
        sx={{
          marginTop: rem(6),
          fontSize: rem(12.5),
          lineHeight: 1.5,
          color: tokens.inkBody,
        }}
      >
        {description}
      </Typography>

      <Box sx={{ marginTop: 'auto', paddingTop: rem(16) }}>{action}</Box>
    </Box>
  );
}

export default RailPromoCard;
